import {
  parse,
  countNodes,
  nodePreview,
  pathKeySegment,
  JsonSyntaxError,
  type JsonNode,
} from '../core/json';
import { createEditor, type EditorHandle } from '../editor/editor';
import { el, button, copyText } from './dom';

const SAMPLE = `{
  "id": 42,
  "name": "example",
  "active": true,
  "scores": [10, 20, 30],
  "meta": { "nested": { "deep": [1, 2, { "x": true }] }, "tags": ["a", "b"] }
}`;

/** Above this size we require an explicit build instead of parsing on keystroke. */
const AUTO_PARSE_MAX_BYTES = 512_000;
/** Above this node count Expand-all is disabled (it would defeat laziness). */
const EXPAND_ALL_MAX_NODES = 5_000;

type Container = Extract<JsonNode, { type: 'object' | 'array' }>;

function isContainer(node: JsonNode): node is Container {
  return node.type === 'object' || node.type === 'array';
}

function valueClass(node: JsonNode): string {
  switch (node.type) {
    case 'string':
      return 'tv-string';
    case 'number':
      return 'tv-number';
    case 'literal':
      return node.value === null ? 'tv-null' : 'tv-bool';
    default:
      return 'tv-preview';
  }
}

/** Tree tab: left editor drives a lazily-rendered, collapsible tree on the right. */
export function createTreeView(): HTMLElement {
  let debounce: number | undefined;
  let expandAll: (() => void) | null = null;
  let collapseAll: (() => void) | null = null;
  let sourceText = '';

  const status = el('div', { class: 'status', role: 'status', 'aria-live': 'polite' });
  const treeHost = el('div', { class: 'tree-host' });
  const editorHost = el('div', { class: 'editor-host' });

  const editor: EditorHandle = createEditor({
    parent: editorHost,
    placeholder: 'Paste or type JSON here…',
    onChange: () => schedule(),
  });

  function schedule(): void {
    if (debounce !== undefined) window.clearTimeout(debounce);
    const text = editor.getValue();
    if (text.length > AUTO_PARSE_MAX_BYTES) {
      renderNeedsBuild(text.length);
      return;
    }
    debounce = window.setTimeout(render, 250);
  }

  function setStatus(state: string, ...children: Array<Node | string>): void {
    status.dataset.state = state;
    status.replaceChildren(...children);
  }

  function renderNeedsBuild(bytes: number): void {
    expandAll = null;
    collapseAll = null;
    const buildBtn = button('Build tree', { class: 'btn primary' });
    buildBtn.addEventListener('click', render);
    treeHost.replaceChildren(
      el('div', { class: 'tree-empty' }, [
        el('p', {}, [
          `Large input (${(bytes / 1000).toFixed(0)} KB). Building on every keystroke is disabled to stay responsive.`,
        ]),
        buildBtn,
      ]),
    );
    setStatus('idle', el('span', {}, ['Press “Build tree” to render.']));
  }

  function render(): void {
    const text = editor.getValue();
    expandAll = null;
    collapseAll = null;

    if (text.trim() === '') {
      treeHost.replaceChildren(
        el('div', { class: 'tree-empty' }, [
          el('p', {}, ['The tree of your JSON will appear here.']),
        ]),
      );
      setStatus('idle', el('span', {}, ['Ready. Your JSON stays in this browser.']));
      return;
    }

    let root: JsonNode;
    try {
      root = parse(text);
    } catch (e) {
      if (e instanceof JsonSyntaxError) {
        treeHost.replaceChildren(
          el('div', { class: 'tree-empty' }, [
            el('p', { class: 'tree-error' }, ['Cannot build tree - JSON is invalid.']),
          ]),
        );
        const { line, column } = e.pos;
        setStatus(
          'error',
          el('span', { class: 'badge badge-error' }, ['Invalid']),
          el('span', {}, [`Line ${line}, Col ${column}: ${e.message}`]),
        );
        return;
      }
      throw e;
    }

    const total = countNodes(root);
    sourceText = text;
    const tree = el('div', { class: 'tree', role: 'tree' });
    const rootRow = renderNode(root, '$', 'root');
    tree.append(rootRow.element);
    treeHost.replaceChildren(tree);
    rootRow.expand?.();

    if (total <= EXPAND_ALL_MAX_NODES) {
      expandAll = () => rootRow.expandDeep?.();
      collapseAll = () => rootRow.collapse?.();
    }

    setStatus(
      'ok',
      el('span', { class: 'badge badge-ok' }, ['Valid']),
      el('span', {}, [`${total} node${total === 1 ? '' : 's'}`]),
      total > EXPAND_ALL_MAX_NODES
        ? el('span', { class: 'badge badge-warn' }, ['Expand-all disabled (large)'])
        : '',
    );
  }

  interface RowHandle {
    element: HTMLElement;
    expand?: () => void;
    collapse?: () => void;
    expandDeep?: () => void;
  }

  function renderNode(node: JsonNode, path: string, keyLabel: string): RowHandle {
    const keyEl = el('span', { class: 'tv-key' }, [keyLabel]);
    const colon = el('span', { class: 'tv-colon' }, [': ']);

    const pathBtn = button('path', { class: 'tv-action', title: `Copy path: ${path}` });
    pathBtn.addEventListener('click', async (ev) => {
      ev.stopPropagation();
      await copyWithFeedback(pathBtn, path);
    });

    if (!isContainer(node)) {
      const valEl = el('span', { class: valueClass(node) }, [nodePreview(node)]);
      const copyValBtn = button('value', { class: 'tv-action', title: 'Copy value' });
      copyValBtn.addEventListener('click', async (ev) => {
        ev.stopPropagation();
        await copyWithFeedback(copyValBtn, node.type === 'string' ? valueOfString(node) : nodePreview(node));
      });
      const row = el('div', { class: 'tree-row leaf', role: 'treeitem' }, [
        el('span', { class: 'tv-toggle-spacer' }, []),
        keyEl,
        colon,
        valEl,
        pathBtn,
        copyValBtn,
      ]);
      return { element: el('div', { class: 'tree-node' }, [row]) };
    }

    const size = node.type === 'object' ? node.members.length : node.elements.length;
    const toggle = el('span', { class: 'tv-toggle', 'aria-hidden': 'true' }, ['▸']);
    const preview = el('span', { class: 'tv-preview' }, [nodePreview(node)]);
    const copyValBtn = button('value', { class: 'tv-action', title: 'Copy this subtree as JSON' });
    copyValBtn.addEventListener('click', async (ev) => {
      ev.stopPropagation();
      await copyWithFeedback(copyValBtn, sourceText.slice(node.start, node.end));
    });
    const row = el('div', { class: 'tree-row', role: 'treeitem', 'aria-expanded': 'false' }, [
      toggle,
      keyEl,
      colon,
      preview,
      pathBtn,
      copyValBtn,
    ]);
    const childrenBox = el('div', { class: 'tree-children', hidden: true }, []);
    const wrapper = el('div', { class: 'tree-node' }, [row, childrenBox]);

    const childHandles: RowHandle[] = [];
    let built = false;
    let expanded = false;

    function buildChildren(): void {
      if (built) return;
      built = true;
      if (node.type === 'object') {
        for (const m of node.members) {
          const childPath = path + pathKeySegment(m.key);
          const h = renderNode(m.value, childPath, m.key);
          childHandles.push(h);
          childrenBox.append(h.element);
        }
      } else if (node.type === 'array') {
        node.elements.forEach((child, i) => {
          const h = renderNode(child, `${path}[${i}]`, String(i));
          childHandles.push(h);
          childrenBox.append(h.element);
        });
      }
    }

    function setExpanded(next: boolean): void {
      if (next && size === 0) return;
      expanded = next;
      if (expanded) buildChildren();
      childrenBox.hidden = !expanded;
      toggle.textContent = expanded ? '▾' : '▸';
      row.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      row.classList.toggle('expanded', expanded);
    }

    row.addEventListener('click', () => setExpanded(!expanded));

    return {
      element: wrapper,
      expand: () => setExpanded(true),
      collapse: () => {
        for (const h of childHandles) h.collapse?.();
        setExpanded(false);
      },
      expandDeep: () => {
        setExpanded(true);
        for (const h of childHandles) h.expandDeep?.();
      },
    };
  }

  const expandBtn = button('Expand all');
  expandBtn.addEventListener('click', () => expandAll?.());
  const collapseBtn = button('Collapse all');
  collapseBtn.addEventListener('click', () => collapseAll?.());

  const sampleBtn = button('Load sample');
  sampleBtn.addEventListener('click', () => {
    editor.setValue(SAMPLE);
    render();
  });

  const clearBtn = button('Clear');
  clearBtn.addEventListener('click', () => {
    editor.setValue('');
    render();
  });

  const toolbar = el('div', { class: 'toolbar' }, [
    expandBtn,
    collapseBtn,
    el('span', { class: 'spacer' }),
    sampleBtn,
    clearBtn,
  ]);

  const split = el('div', { class: 'tree-split' }, [editorHost, treeHost]);

  render();
  return el('section', { class: 'panel' }, [toolbar, split, status]);
}

function valueOfString(node: JsonNode): string {
  return node.type === 'string' ? node.value : nodePreview(node);
}

async function copyWithFeedback(btn: HTMLButtonElement, text: string): Promise<void> {
  const original = btn.textContent ?? '';
  const ok = await copyText(text);
  btn.textContent = ok ? '✓' : '✗';
  window.setTimeout(() => (btn.textContent = original), 900);
}
